import React, { useState } from 'react';
import { useApi, fmtNum } from '../hooks/useApi';

export default function FundamentalPanel() {
  const [marketFilter, setMarketFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selectedTicker, setSelectedTicker] = useState(null);

  // 새 API: KR 50 + US 50
  const { data: allData, loading, error, refetch } = useApi('/api/fundamental/all');
  // 선택 종목 시세
  const { data: priceData } = useApi(
    selectedTicker ? `/api/fundamental/prices?ticker=${selectedTicker}&limit=5` : '/api/fundamental/prices?limit=0',
    [selectedTicker]
  );

  // KR/US 필터 적용
  const krList = allData?.kr || [];
  const usList = allData?.us || [];
  const allList = allData?.all || [];

  let stockList;
  if (marketFilter === 'KR') stockList = krList;
  else if (marketFilter === 'US') stockList = usList;
  else stockList = allList; // 시총(USD 환산) 기준 정렬됨

  // 검색 필터
  const filtered = stockList.filter((s) => {
    if (!search) return true;
    const q = search.toUpperCase();
    return s.ticker.includes(q) || (s.company_name || '').toUpperCase().includes(q);
  });

  const selectedPrice = priceData && priceData.length > 0 ? priceData[0] : null;
  const isKR = selectedTicker && /^\d{6}$/.test(selectedTicker);
  const ccy = isKR ? '₩' : '$';

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-3 border-b border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-400">펀더멘털</h3>
        <span className="text-xs text-gray-600">
          KR {krList.length} + US {usList.length} = {(allData?.total) || 0}
        </span>
      </div>
      {error && (
        <div role="alert" className="px-3 py-2 text-xs text-red-300">
          펀더멘털 조회 실패: {error} <button onClick={refetch}>다시 시도</button>
        </div>
      )}
      {allData?.stale && (
        <div className="px-3 py-2 text-xs text-yellow-300 bg-yellow-900/20 border-b border-yellow-700/40">
          ⚠️ {allData.collection_message} (최근 수집: {allData.latest_screened_date || '없음'})
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-700">
        {/* 좌측: 종목 리스트 */}
        <div className="p-3">
          <div className="flex gap-2 mb-2">
            {['ALL', 'KR', 'US'].map((m) => (
              <button key={m} onClick={() => setMarketFilter(m)}
                className={`px-2 py-1 text-xs rounded min-h-[32px] ${marketFilter === m ? 'bg-blue-600' : 'bg-gray-700 text-gray-400'}`}
              >{m === 'ALL' ? `전체` : m}</button>
            ))}
            <input
              type="text" placeholder="검색 (종목코드/이름)" value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-gray-700 text-xs rounded px-2 py-1 text-gray-300 outline-none min-h-[32px]"
            />
          </div>

          {loading ? (
            <div className="text-xs text-gray-500 p-4">로딩 중...</div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {/* 헤더 */}
              <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-500 border-b border-gray-700 sticky top-0 bg-gray-800">
                <span className="w-6 text-center">#</span>
                <span className="w-16">종목</span>
                <span className="flex-1">종목명</span>
                <span className="w-14 text-right" title="5명의 AI 전문가(버핏·린치·달리오·캐시우드·리버모어)가 각자 기준으로 채점한 종합 점수. 0~100점, 높을수록 매력적.">
                  점수 <span className="text-gray-600 cursor-help">ⓘ</span>
                </span>
                <span className="w-12 text-center" title="점수 60점 이상이면 PASS(투자 후보), 미만이면 FAIL. PASS 종목만 실제 매매 대상이 됩니다.">
                  통과 <span className="text-gray-600 cursor-help">ⓘ</span>
                </span>
                {marketFilter === 'ALL' && (
                  <span className="w-16 text-right" title="시가총액(USD 백만달러 환산). KR 종목은 억원→USD 변환. 전체 탭은 시총 기준 정렬됩니다.">
                    시총 <span className="text-gray-600 cursor-help">ⓘ</span>
                  </span>
                )}
              </div>

              {filtered.length === 0 ? (
                <div className="text-xs text-gray-500 p-4 text-center">종목 없음</div>
              ) : filtered.map((s, i) => (
                <div key={`${s.ticker}-${s.bot_name}-${i}`}
                  onClick={() => setSelectedTicker(s.ticker)}
                  className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer text-xs hover:bg-gray-700 transition border-b border-gray-700/30
                    ${selectedTicker === s.ticker ? 'bg-gray-700 ring-1 ring-blue-500' : ''}`}
                >
                  <span className="w-6 text-center text-gray-500">{s.rank || i + 1}</span>
                  <span className="w-16 font-medium flex items-center gap-1">
                    {s.ticker}
                    <span className={`text-[10px] px-1 rounded ${
                      s.market === 'US' ? 'bg-blue-900/60 text-blue-300' : 'bg-orange-900/60 text-orange-300'
                    }`}>{s.market}</span>
                  </span>
                  <span className="flex-1 text-gray-400 truncate">{s.company_name}</span>
                  <span
                    className={`w-14 text-right font-medium ${s.score >= 80 ? 'text-green-400' : s.score >= 50 ? 'text-yellow-400' : 'text-gray-500'}`}
                    title={`${s.company_name} 종합점수: ${Number(s.score).toFixed(1)}점 (${s.bot_name} 기준)\n80+ 강력매수 | 60~79 매수후보 | 60미만 관망`}
                  >
                    {s.analysis_unavailable ? '-' : Number(s.score).toFixed(0)}
                  </span>
                  {s.analysis_unavailable ? (
                    <span
                      className="w-12 text-center font-medium text-gray-500"
                      title={`${s.company_name}: 유효한 스크리닝 결과 없음 (LLM 호출 실패 등으로 점수 미산출)`}
                    >
                      분석 불가
                    </span>
                  ) : (
                  <span
                    className={`w-12 text-center font-medium ${s.passed ? 'text-green-400' : 'text-red-400/60'}`}
                    title={(s.passed
                      ? `✅ ${s.company_name}: 스크리닝 통과 (${Number(s.score).toFixed(1)}점 ≥ 60). 매매 후보 종목입니다.`
                      : `❌ ${s.company_name}: 스크리닝 미통과 (${Number(s.score).toFixed(1)}점 < 60). 현재 투자 부적합.`)
                      + (s.latest_attempt_date && s.latest_attempt_date > s.screened_date
                        ? `\n${s.screened_date} 마지막 유효 결과 (${s.latest_attempt_date} 스크리닝은 분석 불가)`
                        : '')}
                  >
                    {s.passed ? '통과' : '실패'}
                  </span>
                  )}
                  {marketFilter === 'ALL' && (
                    <span className="w-16 text-right text-gray-500" title={
                      s.market_cap_usd
                        ? `시총: $${Number(s.market_cap_usd).toLocaleString()}M` + (s.market === 'KR' ? ` (${Number(s.market_cap).toLocaleString()}억원)` : '')
                        : '시총 데이터 없음'
                    }>
                      {s.market_cap_usd ? `$${fmtCap(s.market_cap_usd)}` : '-'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 우측: 종목 상세 */}
        <div className="p-3">
          {!selectedTicker ? (
            <div className="text-sm text-gray-500 flex items-center justify-center h-full min-h-[200px]">
              <span className="hidden lg:inline">좌측에서 종목을 선택하세요</span>
              <span className="lg:hidden">위에서 종목을 선택하세요</span>
            </div>
          ) : (
            <div>
              <h4 className="text-sm font-semibold mb-3">{selectedTicker}</h4>
              {selectedPrice ? (
                selectedPrice.source === 'MOCK_UNAVAILABLE' ? (
                  <div className="text-xs text-yellow-400/80 bg-yellow-900/20 rounded p-3">
                    시세 조회 불가 (모의투자 미지원 종목)
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <InfoRow label="현재가" value={`${ccy}${fmtNum(selectedPrice.current_price)}`} />
                      <InfoRow label="등락" value={`${selectedPrice.change >= 0 ? '+' : ''}${ccy}${fmtNum(Math.abs(selectedPrice.change))}`}
                        color={selectedPrice.change >= 0 ? 'text-green-400' : 'text-red-400'} />
                      <InfoRow label="등락률" value={`${Number(selectedPrice.change_rate).toFixed(2)}%`}
                        color={selectedPrice.change_rate >= 0 ? 'text-green-400' : 'text-red-400'} />
                      <InfoRow label="방향" value={selectedPrice.direction} />
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      기준: {selectedPrice.fetched_at}
                    </div>
                  </div>
                )
              ) : (
                <div className="text-xs text-gray-500">시세 데이터 없음</div>
              )}

              {/* 스크리너 점수 */}
              <ScreenerScore ticker={selectedTicker} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, color = '' }) {
  return (
    <div>
      <div className="text-gray-500">{label}</div>
      <div className={`font-medium ${color}`}>{value}</div>
    </div>
  );
}

function fmtCap(val) {
  if (!val) return '-';
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}T`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}B`;
  return `${Math.round(val)}M`;
}

function ScreenerScore({ ticker }) {
  const { data } = useApi('/api/fundamental/screener', [ticker]);
  const matches = (data || []).filter((s) => s.ticker === ticker);

  if (matches.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-700">
      <div className="text-xs text-gray-500 mb-2">봇별 스크리너 점수</div>
      <div className="space-y-1">
        {matches.map((m, i) => (
          <div key={i} className="flex justify-between text-xs">
            <span className="text-gray-400">{m.bot_name}</span>
            <span className={m.passed ? 'text-green-400' : 'text-gray-500'}>
              {Number(m.score).toFixed(1)} {m.passed ? '통과' : '실패'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
